'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ArrowRight, ChevronDown, ChevronUp, CheckCircle2, ArrowLeft,
  Palette, Code2, ShoppingCart, Search, Monitor, Smartphone,
  Eye, TrendingUp, Layers, Zap, Target, Shield, Scale, Wrench,
  Plug, RefreshCw, DollarSign, CreditCard, Package, BarChart2,
  Users, Globe, Accessibility, Star, Tag, Map, Calculator,
  Heart, Phone, Video, FileText, Bell, MessageSquare, Calendar,
  PieChart, Download, Building, Image as ImageIcon, Filter,
} from 'lucide-react';
import { ServiceData } from '@/lib/services/data';
import { projectsData } from '@/lib/projects/data';

const iconMap: Record<string, React.ElementType> = {
  Palette, Code2, ShoppingCart, Search, Monitor, Smartphone,
  Eye, TrendingUp, Layers, Zap, Target, Shield, Scale, Wrench,
  Plug, RefreshCw, DollarSign, CreditCard, Package, BarChart2,
  Users, Globe, Accessibility: Monitor, Star, Tag, Map, Calculator,
  Heart, Phone, Video, FileText, Bell, MessageSquare, Calendar,
  PieChart, Download, Building, Image: ImageIcon, Filter,
};

const accentClassMap: Record<string, { text: string; bg: string; border: string; badge: string }> = {
  primary: { text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30', badge: 'bg-primary/15 text-primary border-primary/30' },
  accent: { text: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/30', badge: 'bg-accent/15 text-accent border-accent/30' },
  emerald: { text: 'text-emerald', bg: 'bg-emerald/10', border: 'border-emerald/30', badge: 'bg-emerald/15 text-emerald border-emerald/30' },
  amber: { text: 'text-amber', bg: 'bg-amber/10', border: 'border-amber/30', badge: 'bg-amber/15 text-amber border-amber/30' },
  secondary: { text: 'text-secondary', bg: 'bg-secondary/10', border: 'border-secondary/30', badge: 'bg-secondary/15 text-secondary border-secondary/30' },
  rose: { text: 'text-rose', bg: 'bg-rose/10', border: 'border-rose/30', badge: 'bg-rose/15 text-rose border-rose/30' },
};

interface ServicePageProps {
  service: ServiceData;
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border/40 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-muted/30 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-display font-semibold text-sm text-foreground">{question}</span>
        {open ? <ChevronUp size={16} className="text-muted-foreground flex-shrink-0" /> : <ChevronDown size={16} className="text-muted-foreground flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1">
          <p className="text-sm text-muted-foreground leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function ServicePageTemplate({ service }: ServicePageProps) {
  const accent = accentClassMap[service.accentColor] || accentClassMap.primary;
  const whatsappMessage = encodeURIComponent(`Hi, I'm interested in your ${service.title} service. Can we discuss my project?`);
  const whatsappUrl = `https://wa.me/919001612303?text=${whatsappMessage}`;

  const relatedProjects = service.relatedProjects
    .map((slug) => projectsData.find((p) => p.slug === slug))
    .filter(Boolean) as typeof projectsData;

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 aurora-bg opacity-60" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-foreground mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <Link href="/services" className="hover:text-foreground transition-colors">Services</Link>
            <span>/</span>
            <span className="text-foreground">{service.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className={`inline-flex items-center gap-1.5 text-xs font-mono-code px-3 py-1.5 rounded-full border ${accent.badge} mb-4`}>
                {service.category}
              </span>
              <h1 className="font-display font-bold text-hero-xl text-foreground mb-4 leading-tight">
                {service.title}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
                {service.shortDescription}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link href="/#contact" className="btn-primary text-sm">
                  Discuss Your Project <ArrowRight size={14} />
                </Link>
                <Link href="/projects" className="btn-outline text-sm">
                  View Our Work <ArrowRight size={14} />
                </Link>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
              <Image
                src={service.heroImage}
                alt={service.heroImageAlt}
                fill
                className="object-cover"
                priority
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">Overview</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-6">
              What is {service.title}?
            </h2>
            {service.longDescription.split('\n\n').map((para, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed mb-4 text-base">
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-muted/10">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="section-label">Why It Matters</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
              Why {service.title} Matters for Your Business
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {service.benefits.map((benefit, i) => {
              const BenefitIcon = iconMap[benefit.icon] || CheckCircle2;
              return (
                <div key={i} className="glass-card rounded-2xl p-6 border border-border/40 hover:border-primary/30 transition-all duration-300 group">
                  <div className={`w-11 h-11 rounded-xl ${accent.bg} flex items-center justify-center mb-4`}>
                    <BenefitIcon size={20} className={accent.text} />
                  </div>
                  <h3 className="font-display font-bold text-base text-foreground mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="section-label">What We Deliver</span>
              <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-6">
                Deliverables
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Every {service.title.toLowerCase()} project includes a defined set of deliverables. Here is what you can expect when working with Webmint.
              </p>
              {service.deliverables.map((group, i) => (
                <div key={i} className="mb-6">
                  <h3 className="font-display font-semibold text-sm text-foreground mb-4">{group.title}</h3>
                  <ul className="space-y-2.5">
                    {group.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <CheckCircle2 size={15} className={`${accent.text} flex-shrink-0 mt-0.5`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="glass-card rounded-2xl p-7 border border-border/40">
              <h3 className="font-display font-bold text-base text-foreground mb-5">Technologies We Use</h3>
              <div className="flex flex-wrap gap-2.5">
                {service.technologies.map((tech, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <span className={`px-3 py-1.5 rounded-lg text-sm font-display font-semibold border ${accent.badge}`}>
                      {tech.name}
                    </span>
                    <span className="text-[10px] font-mono-code text-muted-foreground/60">{tech.category}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-muted/10">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="section-label">How We Work</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
              Our Process
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-base">
              A structured, transparent process that keeps you informed at every stage.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {service.process.map((step, i) => (
              <div key={i} className="glass-card rounded-2xl p-6 border border-border/40 relative overflow-hidden group hover:border-primary/30 transition-all duration-300">
                <span className={`font-mono-code text-4xl font-bold ${accent.text} opacity-20 absolute top-4 right-5 select-none`}>
                  {step.step}
                </span>
                <span className={`inline-block font-mono-code text-xs font-bold ${accent.text} mb-3`}>{step.step}</span>
                <h3 className="font-display font-bold text-base text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="section-label">Common Challenges</span>
            <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
              Problems We Solve
            </h2>
          </div>
          <div className="space-y-4 max-w-3xl mx-auto">
            {service.problemSolutions.map((ps, i) => (
              <div key={i} className="glass-card rounded-xl border border-border/40 overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="p-5 border-b sm:border-b-0 sm:border-r border-border/40 bg-rose/5">
                    <span className="text-xs font-mono-code text-rose/70 uppercase tracking-widest block mb-2">Problem</span>
                    <p className="text-sm font-display font-semibold text-foreground">{ps.problem}</p>
                  </div>
                  <div className="p-5 bg-emerald/5">
                    <span className="text-xs font-mono-code text-emerald/70 uppercase tracking-widest block mb-2">Solution</span>
                    <p className="text-sm font-display font-semibold text-foreground">{ps.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-20 bg-muted/10">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="section-label">Our Work</span>
                <h2 className="font-display font-bold text-section-title text-foreground mt-2">
                  Related Projects
                </h2>
              </div>
              <Link href="/projects" className="btn-outline text-sm hidden sm:flex">
                View All Projects <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedProjects.map((project) => (
                <Link
                  key={project.slug}
                  href={`/projects/${project.slug}`}
                  className="group glass-card rounded-2xl overflow-hidden border border-border/40 hover:border-primary/30 card-hover block"
                >
                  <div className="relative h-48 overflow-hidden">
                    <Image
                      src={project.heroImage}
                      alt={project.heroImageAlt}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                    <span className="absolute top-3 left-3 text-xs font-mono-code px-2.5 py-1 rounded-full bg-background/70 border border-border/60 text-muted-foreground">
                      {project.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-base text-foreground mb-2 group-hover:text-accent transition-colors">{project.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">{project.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {project.technologies.slice(0, 3).map((t) => (
                        <span key={t} className="text-[11px] font-mono-code px-2 py-0.5 bg-muted/60 rounded text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6 sm:hidden">
              <Link href="/projects" className="btn-outline text-sm">
                View All Projects <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="section-label">FAQ</span>
              <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-3">
              {service.faqs.map((faq, i) => (
                <FAQItem key={i} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 aurora-bg opacity-50" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-section-title text-foreground mb-4">
            Ready to Build Something Better?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8 text-base">
            Tell us what you're building and let's discuss the right solution for your business.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link href="/#contact" className="btn-primary">
              Discuss Your Project <ArrowRight size={14} />
            </Link>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="16" height="16" fill="currentColor" className="text-emerald">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.469 2.027 7.773L0 32l8.437-2.007A15.93 15.93 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm7.27 20.122c-.398-.199-2.355-1.162-2.72-1.295-.365-.133-.63-.199-.896.199-.265.398-1.029 1.295-1.261 1.561-.232.265-.465.298-.863.1-.398-.199-1.681-.619-3.203-1.977-1.184-1.057-1.983-2.362-2.215-2.76-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.664.133-.265.066-.497-.033-.697-.1-.199-.896-2.16-1.228-2.958-.323-.776-.651-.671-.896-.683l-.763-.013c-.265 0-.697.1-1.062.497-.365.398-1.394 1.362-1.394 3.322s1.427 3.854 1.626 4.12c.199.265 2.808 4.287 6.803 6.014.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.765-.114 2.355-.963 2.688-1.893.332-.93.332-1.727.232-1.893-.099-.166-.365-.265-.763-.464z"/>
              </svg>
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Back to Services */}
      <div className="py-8 border-t border-border/30">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <Link href="/services" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={14} />
            Back to Services
          </Link>
        </div>
      </div>
    </main>
  );
}
